import React, { useState, useEffect } from "react"
import Button from "@mui/material/Button"
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded"
import LoadingScreen from "components/common/LoadingScreen"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import { PERMISSIONS } from "constants/permission"
import { UsePermissionVerification } from "components/customHooks"
import Paper from "@mui/material/Paper"
import { useSnackbar } from "notistack"
import { verifyResponse } from "utils/httpRequest"
import PageHeading from "components/common/LayoutComponents/PageHeading"
import {
  getSubscriptionGroups,
  getSubscriptionProducts,
  pubSubscriptionProduct,
  cancelPubSubscriptionProduct,
} from "services/subscription"
import SubscriptionProductDlg from "./SubscriptionProductDlg"
import {
  StyledSubscriptionProduct,
  StyledSubscriptionProductTable,
} from "./SubscriptionProduct.style"

const SubscriptionProduct = () => {
  const [loading, setLoading] = useState(false)
  const [subscriptionProducts, setSubscriptionProducts] = useState([])
  const [subscriptionGroups, setSubscriptionGroups] = useState([])

  const { enqueueSnackbar } = useSnackbar()

  const AccessControl = UsePermissionVerification()

  const [
    openSubscriptionProductDialog,
    subscriptionProductDialog,
    primeDialogLoading,
  ] = SubscriptionProductDlg({
    onSubmit: () => {
      refreshSubscriptionProduct()
    },
    subscriptionGroups,
    access: AccessControl.Access,
  })

  useEffect(() => {
    AccessControl.SetAccess({
      ...AccessControl.Access,
      listSubscriptionProductAccess: AccessControl.VerifyAccess([
        PERMISSIONS.SUBSCRIPTION.SUBSCRIPTION_GET_ALL_PRODUCT_LIST,
      ]),
      addSubscriptionProductAccess: AccessControl.VerifyAccess([
        PERMISSIONS.SUBSCRIPTION.SUBSCRIPTION_ADD_PRODUCT,
      ]),
      editSubscriptionProductAccess: AccessControl.VerifyAccess([
        PERMISSIONS.SUBSCRIPTION.SUBSCRIPTION_EDIT_PRODUCT,
      ]),
      pubSubscriptionProductAccess: AccessControl.VerifyAccess([
        PERMISSIONS.SUBSCRIPTION.SUBSCRIPTION_PUB_PRODUCT,
      ]),
      cancelPubSubscriptionProductAccess: AccessControl.VerifyAccess([
        PERMISSIONS.SUBSCRIPTION.SUBSCRIPTION_CANCEL_PUB_PRODUCT,
      ]),
      listSubscriptionPlatformProductAccess: AccessControl.VerifyAccess([
        PERMISSIONS.SUBSCRIPTION.SUBSCRIPTION_LIST_PLATFORM_PRODUCT,
      ]),
      addSubscriptionPlatformProductAccess: AccessControl.VerifyAccess([
        PERMISSIONS.SUBSCRIPTION.SUBSCRIPTION_ADD_PLATFORM_PRODUCT,
      ]),
      editSubscriptionPlatformProductAccess: AccessControl.VerifyAccess([
        PERMISSIONS.SUBSCRIPTION.SUBSCRIPTION_EDIT_PLATFORM_PRODUCT,
      ]),
      deleteSubscriptionPlatformProductAccess: AccessControl.VerifyAccess([
        PERMISSIONS.SUBSCRIPTION.SUBSCRIPTION_DELETE_PLATFORM_PRODUCT,
      ]),
    })
  }, [AccessControl.UserPermissions])

  const refreshSubscriptionProduct = async () => {
    setLoading(true)
    try {
      const response = await getSubscriptionProducts()
      verifyResponse(response)
      setSubscriptionProducts(response.data || [])
    } finally {
      setLoading(false)
    }
  }

  const refreshSubscriptionGroup = async () => {
    const response = await getSubscriptionGroups()
    setSubscriptionGroups(response.data || [])
  }

  const pubProduct = async (productId) => {
    setLoading(true)
    try {
      const response = await pubSubscriptionProduct(productId)
      if (response.rtnCode === 200) {
        enqueueSnackbar("Publish subscription product Success", {
          variant: "success",
        })
      } else {
        enqueueSnackbar("Fail to publish product", {
          variant: "warning",
        })
      }
      await refreshSubscriptionProduct()
    } finally {
      setLoading(false)
    }
  }

  const cancelPubProduct = async (productId) => {
    setLoading(true)
    try {
      const response = await cancelPubSubscriptionProduct(productId)
      if (response.rtnCode === 200) {
        enqueueSnackbar("Cancel publish product Success", {
          variant: "success",
        })
      } else {
        enqueueSnackbar("Fail to cancel publish product", {
          variant: "warning",
        })
      }
      await refreshSubscriptionProduct()
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    // fetch group list
    refreshSubscriptionGroup()
    // fetch product list
    refreshSubscriptionProduct()
  }, [])

  return (
    <StyledSubscriptionProduct>
      <PageHeading>
        <div className="heading-copy">
          <h1 className="page-title">Subscription Products</h1>
          <p className="page-subtitle">
            Manage membership product details, pricing, trial rules, and publish
            status.
          </p>
        </div>
        <div className="actions">
          {AccessControl.Access.addSubscriptionProductAccess && (
            <Button
              className="new-product-btn"
              variant="contained"
              size="medium"
              startIcon={<AddCircleOutlineRoundedIcon />}
              onClick={() => {
                openSubscriptionProductDialog()
              }}
            >
              Add New Product
            </Button>
          )}
        </div>
      </PageHeading>
      <div className="page-surface">
        {AccessControl.Access.listSubscriptionProductAccess && (
          <StyledSubscriptionProductTable>
            {!subscriptionProducts || subscriptionProducts.length === 0 ? (
              <div className="empty-group-des">
                No subscription products available yet.
              </div>
            ) : (
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label="subscription products table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Code</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="left">Group</TableCell>
                      <TableCell align="center">Duration</TableCell>
                      <TableCell align="center">Price</TableCell>
                      <TableCell align="left">Subscription Type</TableCell>
                      <TableCell align="center">Trial</TableCell>
                      <TableCell align="center">Rank</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {subscriptionProducts.map((row) => (
                      <TableRow
                        key={`program-table-row-${row.id}`}
                        sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                      >
                        <TableCell component="th" scope="row">
                          {row.name}
                        </TableCell>
                        <TableCell align="left">{row.code}</TableCell>
                        <TableCell align="left">
                          <span
                            className={`status-chip ${
                              row.pubStatus ? "published" : "unpublished"
                            }`}
                          >
                            {row.pubStatus ? "Published" : "Unpublished"}
                          </span>
                        </TableCell>
                        <TableCell align="left">{row.groupName}</TableCell>
                        <TableCell align="center">{row.duration}</TableCell>
                        <TableCell align="center">{row.price}</TableCell>
                        <TableCell align="center">{row.subscriptionType}</TableCell>
                        <TableCell align="center">
                          {row.trialFlag ? `${row.trialDuration} days` : "No trial"}
                        </TableCell>
                        <TableCell align="center">{row.programRank}</TableCell>
                        <TableCell align="right">
                          <div className="row-actions">
                            {row.pubStatus === 1 &&
                              AccessControl.Access
                                .cancelPubSubscriptionProductAccess && (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => {
                                    cancelPubProduct(row.id)
                                  }}
                                >
                                  Unpublish
                                </Button>
                              )}
                            {row.pubStatus === 0 &&
                              AccessControl.Access.pubSubscriptionProductAccess && (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => {
                                    pubProduct(row.id)
                                  }}
                                >
                                  Publish
                                </Button>
                              )}
                            {AccessControl.Access.editSubscriptionProductAccess && (
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => {
                                  openSubscriptionProductDialog(row)
                                }}
                              >
                                Edit
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </StyledSubscriptionProductTable>
        )}
        {!AccessControl.Access.listSubscriptionProductAccess && (
          <div className="page-empty">
            You do not have permission to view subscription products.
          </div>
        )}
      </div>
      {subscriptionProductDialog}
      <LoadingScreen loading={loading || primeDialogLoading} />
    </StyledSubscriptionProduct>
  )
}

export default SubscriptionProduct
